import { dollars } from './buildings';

/*
 * Money tools: bank loans repaid in daily instalments, and the fees the airport charges.
 *
 * Fees are percentages of the standard tariff. Higher fees earn more per flight, but airlines
 * make fewer offers and like their contracts less, and passengers grumble about the price.
 */

export interface Loan {
  readonly id: number;
  readonly principal: number;
  /** Still to pay, interest included (cents). */
  remaining: number;
  /** Daily instalment (cents). */
  readonly payment: number;
}

/** Loan sizes the bank offers. */
export const LOAN_OPTIONS: readonly number[] = [
  dollars(100_000),
  dollars(250_000),
  dollars(500_000),
];
/** Interest over the whole loan, percent. */
export const LOAN_INTEREST_PERCENT = 12;
/** Repaid in this many daily instalments. */
export const LOAN_DAYS = 10;
export const MAX_LOANS = 3;

/** Total to repay and the daily instalment for a loan of `principal`. */
export function loanTerms(principal: number): { total: number; payment: number } {
  const total = Math.floor((principal * (100 + LOAN_INTEREST_PERCENT)) / 100);
  return { total, payment: Math.ceil(total / LOAN_DAYS) };
}

/** Paying a loan off early waives the interest not yet due: the principal share of what is left. */
export function payoffAmount(loan: Loan): number {
  return Math.ceil((loan.remaining * 100) / (100 + LOAN_INTEREST_PERCENT));
}

/**
 * Overdraft: a negative balance costs interest every hour (per mille a day), and airlines wary
 * of an airport in the red make half as many offers.
 */
export const OVERDRAFT_PERMILLE_PER_DAY = 6;
export const OVERDRAFT_OFFER_PERCENT = 50;

/** Interest for one hour on a negative balance (cents; 0 when in credit). */
export function overdraftInterest(money: number): number {
  if (money >= 0) return 0;
  return Math.floor((-money * OVERDRAFT_PERMILLE_PER_DAY) / 1000 / 24);
}

/** Buildings count towards the airport's value at this share (percent) of what they cost. */
export const ASSET_VALUE_PERCENT = 60;

/**
 * What the airport is worth (cents): its buildings at book value, plus cash, less what is
 * still owed to the bank.
 */
export function airportValue(w: {
  readonly money: number;
  readonly buildings: readonly { readonly spent: number }[];
  readonly loans: readonly Loan[];
}): number {
  let assets = 0;
  for (const b of w.buildings) assets += Math.floor((b.spent * ASSET_VALUE_PERCENT) / 100);
  let debt = 0;
  for (const l of w.loans) debt += l.remaining;
  return assets + w.money - debt;
}

/** Fee settings, percent of the standard tariff. */
export const FEE_MIN = 50;
export const FEE_MAX = 200;
export const FEE_STEP = 5;
export const FEE_DEFAULT = 100;

export function validFee(pct: number): boolean {
  return Number.isInteger(pct) && pct >= FEE_MIN && pct <= FEE_MAX && pct % FEE_STEP === 0;
}

/** Airline offers per hour, per mille of the base rate: cheap airports hear from more airlines. */
export function offerRatePermille(landingPct: number, paxPct: number): number {
  const avg = (landingPct + paxPct) / 2;
  return Math.max(250, Math.min(1500, Math.floor(1000 * (2 - avg / 100))));
}

/** Points off an airline's contract score per departure for fees above the tariff. */
export function feeGrudge(landingPct: number, paxPct: number): number {
  return Math.max(0, Math.floor(((landingPct + paxPct) / 2 - 100) / 2));
}

/** Points off passenger satisfaction for a passenger fee above the tariff. */
export function fareGrudge(paxPct: number): number {
  return Math.max(0, Math.floor((paxPct - 100) / 4));
}

export interface LoanWorld {
  money: number;
  readonly loans: Loan[];
}

/** Daily: every loan takes its instalment. Returns the total paid. */
export function payInstalments(w: LoanWorld): number {
  let paid = 0;
  for (let i = w.loans.length - 1; i >= 0; i--) {
    const loan = w.loans[i] as Loan;
    const p = Math.min(loan.payment, loan.remaining);
    loan.remaining -= p;
    paid += p;
    if (loan.remaining <= 0) w.loans.splice(i, 1);
  }
  w.money -= paid;
  return paid;
}
